<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import CopyTrackWrapper from '@/components/CopyTrackWrapper.vue'
import WikiText from '@/components/wikitext/WikiText.vue'
import ResourceLanguageDropdown from '@/resources/ResourceLanguageDropdown.vue'
import ResourceListContainer from '@/resources/ResourceListContainer.vue'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import BigGoal from '../BigGoal.vue'
import CollapsibleGoalSection from '../CollapsibleGoalSection.vue'
import GeneratedAssessmentTile from '../assessments/GeneratedAssessmentTile.vue'
import { useSavedPageGeneratedAssessments } from '../assessments/useSavedPageGeneratedAssessments'
import AccordionObjectivesList from '../objectives/AccordionObjectivesList.vue'
import { useAnchorPageStore } from '../anchor_page.store'
import { useGoalResourcesStore } from '../goal_resources.store'
import { useAnchorPageTracking } from '../useAnchorPageTracking'
import { useObjectiveResourceMaps } from '../anchor-page/useObjectiveResourceMaps'

const BIG_GOAL_EVENT_CONTEXT = { obj_level: 'Annual' }

// Copies of the annual target are reported against the shared copy, as Vue 2 did.
const props = defineProps<{
  savedPageUuid: string
  savedTitle: string
  savedGoalText?: string
}>()

const anchorPageStore = useAnchorPageStore()
const { content, hasPMAs, hasAccordionObjectives, accessInfo, strategyMap } = storeToRefs(anchorPageStore)
const goalResourcesStore = useGoalResourcesStore()
const { trackQuarterlyObjectivesToggled } = useAnchorPageTracking(getAnalyticsTracker())
const { annualAssessments, assessmentsForLevel, levelsWithGeneratedAssessments } = useSavedPageGeneratedAssessments(() => props.savedPageUuid)

const contentUuid = computed(() => content.value?.uuid ?? '')
const objectives = computed(() => content.value?.objectives ?? [])
const objectiveTexts = computed(() => objectives.value.map((objective) => objective.text))
const isResourceLanguageDropdownShown = computed(() => goalResourcesStore.hasNonEnglishLangById(contentUuid.value))
const selectedResourceLanguage = computed({
  get: () => goalResourcesStore.selectedLanguageById(contentUuid.value),
  set: (language: string) => goalResourcesStore.setSelectedLanguage(contentUuid.value, language)
})
const isAnnualResourcesSectionShown = computed(() => hasPMAs.value || annualAssessments.value.length > 0)
const { resourcesByObjectId, isFetchingResourcesByObjectId, selectedLanguages } = useObjectiveResourceMaps(objectives)
</script>

<template>
  <h2 v-if="!hasAccordionObjectives" class="mb-6 mt-9 text-display-sm font-medium leading-tight">Annual Target</h2>
  <!-- The owner's goal and objective text often names the student, so it is kept out of session recordings. -->
  <BigGoal :is-accordion-layout="hasAccordionObjectives" :event-context="BIG_GOAL_EVENT_CONTEXT">
    <CopyTrackWrapper
      v-if="savedGoalText"
      type="annual_target"
      level="Annual"
      :title="savedTitle"
      :uuid="savedPageUuid"
      gate-copy
      class="fs-exclude"
      :class="{ 'px-4 py-2 font-regular': hasAccordionObjectives }"
      data-testid="annual-target-text"
    >
      <WikiText :text="savedGoalText" :strategies="strategyMap" />
    </CopyTrackWrapper>
    <ResourceLanguageDropdown
      v-if="isResourceLanguageDropdownShown"
      v-model="selectedResourceLanguage"
      :languages="goalResourcesStore.languagesById(contentUuid)"
    />

    <template #footer>
      <div v-if="isAnnualResourcesSectionShown" data-testid="annual-resources">
        <ResourceListContainer
          :resources="goalResourcesStore.resourcesById(contentUuid)"
          :is-fetching-resources="goalResourcesStore.isFetchingResourcesById(contentUuid)"
          :selected-language="goalResourcesStore.selectedLanguageById(contentUuid)"
          :is-tiled="hasAccordionObjectives"
          :is-indented="hasAccordionObjectives"
        >
          <div v-if="annualAssessments.length > 0" class="flex flex-wrap" :class="{ 'pl-4': !hasAccordionObjectives }">
            <GeneratedAssessmentTile v-for="assessment in annualAssessments" :key="assessment.uuid" :assessment="assessment" />
          </div>
        </ResourceListContainer>
      </div>

      <CollapsibleGoalSection
        v-if="hasAccordionObjectives"
        is-initially-expanded
        is-indented
        data-testid="accordion-objectives-section"
        @toggle="trackQuarterlyObjectivesToggled"
      >
        <template #label>
          <span class="material-icons-round mr-2 align-text-bottom" aria-hidden="true">format_list_bulleted</span>Objectives
        </template>
        <AccordionObjectivesList
          v-model:selected-languages="selectedLanguages"
          class="fs-exclude"
          :objectives="objectives"
          :objective-texts="objectiveTexts"
          :page-uuid="contentUuid"
          :is-locked="accessInfo.blur"
          :strategies="strategyMap"
          :resources-by-object-id="resourcesByObjectId"
          :is-fetching-resources-by-object-id="isFetchingResourcesByObjectId"
          :levels-with-generated-assessments="levelsWithGeneratedAssessments"
        >
          <template #generatedAssessments="{ objective }">
            <GeneratedAssessmentTile
              v-for="assessment in assessmentsForLevel(objective.level)"
              :key="assessment.uuid"
              :assessment="assessment"
            />
          </template>
        </AccordionObjectivesList>
      </CollapsibleGoalSection>
    </template>
  </BigGoal>
</template>
